/* eslint-disable react/jsx-indent */
import React, { FC } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import StyledBox from 'views/rebranded-module/deals-module/DealDetailsViewV2/components/ DealInformationsSection/DealInformationsSection.styles';
import useAdabtiveMultiSizeImage from 'views/common/hooks/useAdabtiveMultiSizeImage';
import { ImgBox, Typography700 } from 'views/rebranded-module/common/components';
import { useContent } from 'views/rebranded-module/common/context/useContent';
import { interpolateString } from 'views/rebranded-module/common/utils/interpolateString';
import { DealInformationItem } from 'views/rebranded-module/deals-module/DealDetailsViewV2/components';
import {
  RiCalendar2Fill,
  RiMapPin2Line,
  RiCalendarEventLine,
  RiCalendar2Line,
} from 'react-icons/ri';
import DirtbikeIcon from 'public/imgs/Rebranding/icons/DirtbikeIcon';
import Typography500 from 'views/common/components/UI/Typography500';

interface DealImageSize {
  url: string;
}
interface DealImage {
  small_url?: DealImageSize;
  medium_url?: DealImageSize;
  large_url?: DealImageSize;
}
interface Deal {
  title: string;
  image: DealImage;
  latest_booking_date?: string;
  earliest_pickup_date?: string;
  latest_pickup_date?: string;
  earliest_dropoff_date?: string;
  latest_dropoff_date?: string;
  max_motorcycles_number?: number;
  min_motorcycles_number?: number;
  max_rental_days?: number;
  min_rental_days?: number;
  description?: string;
  available_for_all_vehicles_in_pickup_locations?: boolean;
  terms_conditions?: string;
}
interface DealInformationsSectionProps {
  deal: Deal;
  vehicleModel?: string;
}

const DealInformationsSection: FC<DealInformationsSectionProps> = (props) => {
  const { deal, vehicleModel } = props;
  const getSizedImage = useAdabtiveMultiSizeImage();
  const content = useContent('DealDetailsPage');
  const dealImage = {
    small_url: {
      url: deal.image.medium_url,
    },
    medium_url: {
      url: deal.image.medium_url,
    },
    large_url: {
      url: deal.image.large_url,
    },
  };
  const hasPickupRange =
    !!deal.earliest_pickup_date && !!deal.latest_pickup_date;

  const hasDropoffRange =
    !!deal.earliest_dropoff_date && !!deal.latest_dropoff_date;

  // Boolean flags for conditional rendering
  const hasPickupOrDropoff = hasPickupRange || hasDropoffRange;
  const hasMaxMotorcycles = !!deal.max_motorcycles_number;
  const hasMinMotorcycles = !!deal.min_motorcycles_number;
  const hasAnyMotorcycles = hasMaxMotorcycles || hasMinMotorcycles;
  const hasMaxRentalDays = !!deal.max_rental_days;
  const hasMinRentalDays = !!deal.min_rental_days;
  const hasAnyRentalDays = hasMaxRentalDays || hasMinRentalDays;

  return (
    <StyledBox>
      <Box className="DIImgContainer" mb={4}>
        <ImgBox
          image={{
            src: getSizedImage(dealImage),
            alt: deal.title,
          }}
          width="100%"
          priority
        />
      </Box>
      <Typography700 variant="h5" component="h4" mb={3}>
        {content.aboutDealLabel}
      </Typography700>

      <Box className="DIItemsContainer" mt={2}>
        {/* Row 1 — Book before */}
        {!!deal.latest_booking_date && (
          <Box className="DIItemRow">
            <DealInformationItem
              title={content.bookBeforeLabel}
              details={deal.latest_booking_date}
              icon={<RiCalendar2Fill className="DIIcon" />}
            />
          </Box>
        )}
        {/* Row 2 — Pickup & Dropoff */}
        {hasPickupOrDropoff && (
          <Box className="DIItemRow">
            <Grid container spacing={1.5}>
              {hasPickupRange && (
                <Grid item xl={6} lg={12} md={12} xs={12}>
                  <DealInformationItem
                    title={content.pickUpBetweenLabel}
                    details={`${deal.earliest_pickup_date} - ${deal.latest_pickup_date}`}
                    icon={<RiMapPin2Line className="DIIcon" />}
                    titleOnSeparateLine
                  />
                </Grid>
              )}

              {hasDropoffRange && (
                <Grid item xl={6} lg={12} md={12} xs={12}>
                  <DealInformationItem
                    title={content.dropOffBetweenLabel}
                    details={`${deal.earliest_dropoff_date} - ${deal.latest_dropoff_date}`}
                    icon={<RiMapPin2Line className="DIIcon" />}
                    titleOnSeparateLine
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}
        {/* Row 3 — Motorcycles */}
        {hasAnyMotorcycles && (
          <Box className="DIItemRow">
            <Grid container spacing={1.5}>
              {hasMaxMotorcycles && (
                <Grid item lg={6} md={6} sm={12}>
                  <DealInformationItem
                    title={content.maxMotorcyclesLabel}
                    details={deal.max_motorcycles_number}
                    icon={<DirtbikeIcon className="DIIcon" />}
                  />
                </Grid>
              )}

              {hasMinMotorcycles && (
                <Grid item lg={6} md={6} sm={12}>
                  <DealInformationItem
                    title={content.minMotorcyclesLabel}
                    details={deal.min_motorcycles_number}
                    icon={<DirtbikeIcon className="DIIcon" />}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}
        {/* Row 4 — Rental days */}
        {hasAnyRentalDays && (
          <Box className="DIItemRow">
            <Grid container spacing={1.5}>
              {hasMaxRentalDays && (
                <Grid item lg={6} md={6} sm={12}>
                  <DealInformationItem
                    title={content.maxRentalDaysLabel}
                    details={deal.max_rental_days}
                    icon={<RiCalendarEventLine className="DIIcon" />}
                  />
                </Grid>
              )}

              {hasMinRentalDays && (
                <Grid item lg={6} md={6} sm={12}>
                  <DealInformationItem
                    title={content.minRentalDaysLabel}
                    details={deal.min_rental_days}
                    icon={<RiCalendar2Line className="DIIcon" />}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Box>
      {deal.description && (
        <Typography
          variant="body2"
          component="div"
          mb={2}
          color="text.secondary"
          dangerouslySetInnerHTML={{ __html: deal.description }}
        />
      )}
      <Box className="DISAvailbelityNote DIDescSpacing">
        {deal.available_for_all_vehicles_in_pickup_locations ? (
          <Typography variant="body3" component="p" color="text.secondary">
            {content.dealValidAllModels}
          </Typography>
        ) : (
          <Typography
            variant="body3"
            component="p"
            color="text.secondary"
            dangerouslySetInnerHTML={{
              __html: interpolateString(content.dealValidSpecificModels, {
                models: vehicleModel,
              }),
            }}
          />
        )}
      </Box>
      {deal.terms_conditions && (
        <Box className="DIDescSpacing">
          <Typography700 variant="h5" component="h5" mb={2}>
            {content.termsConditionsLabel}
          </Typography700>
          <Typography500 variant="body2" component="p" className="DIDesc" color="text.secondary">
            {deal.terms_conditions}
          </Typography500>
        </Box>
      )}
    </StyledBox>
  );
};

export { DealInformationsSection };
